import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const FAQ_SECTION_SELECTOR =
  "[data-wv-faq-section], .wp-block-blocks-webvibe-block-faq";

const getFaqItems = (section) => {
  return Array.from(section.querySelectorAll(".faq-item"));
};

const setFaqItemOpenState = (item, isOpen, animate = true) => {
	const button = item.querySelector("[data-faq-button]");
	const panel = item.querySelector("[data-faq-panel]");
	const openIcon = item.querySelector(".handle-open-item");
	const closeIcon = item.querySelector(".handle-close-item");

	if (!button || !panel) return;

	const prefersReducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)"
	).matches;

	const shouldAnimate = animate && !prefersReducedMotion;

	gsap.killTweensOf(panel);

	button.setAttribute("aria-expanded", String(isOpen));
	panel.classList.toggle("show", isOpen);

	if (openIcon) {
		openIcon.classList.toggle("hideIcon", !isOpen);
	}

	if (closeIcon) {
		closeIcon.classList.toggle("hideIcon", isOpen);
	}

	if (isOpen) {
		panel.hidden = false;

		const targetHeight = panel.scrollHeight;

		if (!shouldAnimate) {
			panel.style.height = "";
			panel.style.opacity = "";
			return;
		}

		gsap.fromTo(
			panel,
			{
				height: 0,
				opacity: 0,
			},
			{
				height: targetHeight,
				opacity: 1,
				duration: 0.55,
				ease: "power3.out",
				onComplete: () => {
					panel.style.height = "auto";
				},
			}
		);

		return;
	}

	const currentHeight = panel.scrollHeight;

	if (!shouldAnimate) {
		panel.hidden = true;
		panel.style.height = "";
		panel.style.opacity = "";
		return;
	}

	gsap.fromTo(
		panel,
		{
			height: currentHeight,
			opacity: 1,
		},
		{
			height: 0,
			opacity: 0,
			duration: 0.42,
			ease: "power2.inOut",
			onComplete: () => {
				panel.hidden = true;
				panel.style.height = "";
				panel.style.opacity = "";
			},
		}
	);
};

const closeOtherFaqItems = (items, currentItem) => {
  items.forEach((item) => {
    if (item !== currentItem) {
      setFaqItemOpenState(item, false, false);
    }
  });
};

const focusFaqButton = (items, index) => {
  const targetItem = items[index];
  const targetButton = targetItem?.querySelector("[data-faq-button]");

  if (targetButton) {
    targetButton.focus();
  }
};

const initFaqAccordion = (section, sectionIndex) => {
  if (section.dataset.wvFaqAccordionInited === "1") return;

  section.dataset.wvFaqAccordionInited = "1";

  const items = getFaqItems(section);

  items.forEach((item, itemIndex) => {
    const button = item.querySelector("[data-faq-button]");
    const panel = item.querySelector("[data-faq-panel]");

    if (!button || !panel) return;

    const buttonId = `wv-faq-${sectionIndex}-${itemIndex}-button`;
    const panelId = `wv-faq-${sectionIndex}-${itemIndex}-panel`;

    button.id = button.id || buttonId;
    panel.id = panel.id || panelId;

    button.setAttribute("aria-controls", panel.id);
    button.setAttribute("aria-expanded", "false");

    panel.setAttribute("role", "region");
    panel.setAttribute("aria-labelledby", button.id);
    panel.hidden = true;

    button.addEventListener("click", () => {
      const isOpen = button.getAttribute("aria-expanded") === "true";

      closeOtherFaqItems(items, item);
      setFaqItemOpenState(item, !isOpen);
    });

    button.addEventListener("keydown", (event) => {
      const lastIndex = items.length - 1;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          focusFaqButton(items, itemIndex === lastIndex ? 0 : itemIndex + 1);
          break;

        case "ArrowUp":
          event.preventDefault();
          focusFaqButton(items, itemIndex === 0 ? lastIndex : itemIndex - 1);
          break;

        case "Home":
          event.preventDefault();
          focusFaqButton(items, 0);
          break;

        case "End":
          event.preventDefault();
          focusFaqButton(items, lastIndex);
          break;

        default:
          break;
      }
    });

    setFaqItemOpenState(item, false, false);
  });
};

const initFaqAnimation = (section) => {
  if (section.dataset.wvAnimInited === "1") return;

  section.dataset.wvAnimInited = "1";

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  if (prefersReducedMotion) return;

  const title = section.querySelector("h2.global_title");
  const desc = section.querySelector(".wv-faq-description");
  const items = gsap.utils.toArray(section.querySelectorAll(".faq-item"));
  const decor = section.querySelector(".wv-decor");

  if (title) gsap.set(title, { autoAlpha: 0, y: 40 });
  if (desc) gsap.set(desc, { autoAlpha: 0, y: 30 });
  if (decor) gsap.set(decor, { autoAlpha: 0, scale: 0.95 });

  gsap.set(items, { autoAlpha: 0, y: 50 });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: "top 75%",
      once: true,
    },
  });

  if (title) {
    tl.to(title, {
      autoAlpha: 1,
      y: 0,
      duration: 0.8,
      ease: "power3.out",
    });
  }

  if (desc) {
    tl.to(
      desc,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
      },
      0.15
    );
  }

  tl.to(
    items,
    {
      autoAlpha: 1,
      y: 0,
      duration: 0.8,
      ease: "power4.out",
      stagger: 0.18,
    },
    0.3
  );

  if (decor) {
    tl.to(
      decor,
      {
        autoAlpha: 1,
        scale: 1,
        duration: 0.8,
        ease: "power3.out",
      },
      0.2
    );

    gsap.to(decor, {
      y: 12,
      duration: 6,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      delay: 1,
    });
  }
};

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(FAQ_SECTION_SELECTOR).forEach((section, index) => {
    initFaqAccordion(section, index);
    initFaqAnimation(section);
  });
});